import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import {
  ApartmentOutlined,
  BadgeOutlined,
  CalendarMonthOutlined,
  CategoryOutlined,
  EditOutlined,
  EventRepeatOutlined,
  ScheduleOutlined,
  StorageOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { ViewActionFooter } from "../Shared/ViewActionFooter";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { SelectedEmployeeHeader } from "pages/TeamZone/Approve/SelectedEmployeeHeader";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { LoaderWithoutHeight } from "components/Loader";
import { tooltipStyleProps } from "services/constants/Styles";
import { PayInputActivityLog } from "../Shared/PayInputActivityLog";
import { DateField, SelectField, TextField, TimeField, toOptions } from "../Shared/fieldInputs";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { Field, FormSection, panelBodySx } from "../Shared/formUi";
import { formatDate, formatExactQty, formatMoney } from "../Shared/formatters";
import { designColors, designShell } from "services/constants/designTokens";
import { BASIS_OPTIONS, validateForm, previewPenalty, quantityValue } from "./penaltiesData";
import { usePenaltyTypes } from "./usePenaltyTypes";
import { useShiftSchedules } from "../ShiftEarnings/useShiftSchedules";
import { loadShiftFormOptions } from "../ShiftEarnings/shiftEarningsApi";
import { useSalaryRateAutofill } from "../Shared/useSalaryRateAutofill";

// Label with the red required asterisk.
const required = (label) => (
  <>
    {label} <Mui.Box component="span" sx={{ color: "#DC2626" }}>*</Mui.Box>
  </>
);

// Pay Inputs > Penalties panels, same layout as the Shift Earnings panels.
// Add + Edit. `initial` is a form object (see emptyForm / rowToForm).
// `source` is the entry's source when editing: Manual and Import entries
// are fully editable; Attendance entries come from the attendance
// calculations, so only their Payroll Cycle can be changed here.
export const PenaltyFormPanel = ({
  open,
  mode,
  initial,
  source,
  onClose,
  onSave,
  expanded,
  onToggleExpand,
  saving,
}) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const [options, setOptions] = useState({ employees: [], shifts: [], errors: [] });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setOptionsLoading(true);
    setOptions({ employees: [], shifts: [], errors: [] });
    loadShiftFormOptions(domain, topbarCompanyId, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setOptions(data); })
      .catch((error) => { if (!controller.signal.aborted) setOptions({ employees: [], shifts: [], errors: [error.message] }); })
      .finally(() => { if (!controller.signal.aborted) setOptionsLoading(false); });
    return () => controller.abort();
  }, [open, domain, topbarCompanyId, retry]);
  // Rows saved before `source` existed are all from attendance.
  const locked = mode === "edit" && (!source || source === "Attendance");
  const [form, setForm] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setShowErrors(false);
    }
  }, [open, initial]);

  // Penalty Type options from GET payroll/deduction/penalty/types/.
  const { types: penaltyTypes, loading: penaltyTypesLoading } = usePenaltyTypes(domain, open);
  // Add starts on a type the server offers (the default may not be one).
  useEffect(() => {
    if (!open || mode !== "add" || penaltyTypesLoading) return;
    setForm((current) =>
      current && !penaltyTypes.includes(current.penalty_type)
        ? { ...current, penalty_type: penaltyTypes.includes("Late Entry") ? "Late Entry" : penaltyTypes[0] }
        : current,
    );
  }, [open, mode, penaltyTypes, penaltyTypesLoading]);

  const errors = useMemo(() => (form ? validateForm(form, mode === "edit" ? initial : null, penaltyTypes) : {}), [form, mode, initial, penaltyTypes]);
  const totals = useMemo(() => (form ? previewPenalty(form, mode === "edit" ? initial : null) : null), [form, mode, initial]);

  const { schedules, error: scheduleError, loading: schedulesLoading, retry: retrySchedules } = useShiftSchedules(domain, [form?.employee_code], open);

  // Rate auto-fill, same rule as Shift Earnings / Leave: the employee's
  // per-day rate for Shift basis, per-hour for Hours. On Add, and on Edit of
  // Manual / Import entries; attendance (locked) entries and Amount basis
  // (no rate) are left alone.
  const employeeUserId = options.employees.find((option) => option.value === form?.employee_code)?.user_id || "";
  useSalaryRateAutofill({
    domain,
    enabled: open && !locked && form?.basis !== "amount",
    userId: employeeUserId,
    payScheduleId: form?.pay_schedule_id,
    date: form?.date,
    basis: form?.basis,
    setForm,
  });

  if (!form) return null;

  const employeeOptions = [...options.employees];
  if (form.employee_code && !employeeOptions.some((option) => option.value === form.employee_code)) employeeOptions.push({ value: form.employee_code, id: form.employee_code, employee_id: form.employee_code, label: initial.employee_name || form.employee_code, image: initial.image_url || "", image_url: initial.image_url || "" });
  const selectedEmployee = employeeOptions.find((option) => option.value === form.employee_code) ?? null;
  const shiftOptions = [...options.shifts];
  if (form.shift_name && !shiftOptions.some((option) => option.value === form.shift_name)) shiftOptions.push({ value: form.shift_name, label: form.shift_name });
  const cycleOptions = [...schedules];
  if (form.pay_schedule_id && !cycleOptions.some((option) => option.value === form.pay_schedule_id)) cycleOptions.push({ value: form.pay_schedule_id, label: initial.payroll_cycle || form.pay_schedule_id });
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value, ...(key === "employee_code" ? { pay_schedule_id: "" } : {}) }));
  const visibleError = (key) => (showErrors ? errors[key] : undefined);

  const handleSave = () => {
    if (Object.keys(errors).length) {
      setShowErrors(true);
      return;
    }
    onSave(form);
  };

  const numberInput = (key) => (
    <TextField
      type="number"
      value={form[key]}
      disabled={locked || saving}
      error={visibleError(key)}
      onChange={set(key)}
    />
  );

  return (
    <GlobalSidePanel
      open={open}
      onClose={saving ? undefined : onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title={mode === "edit" ? "Edit Penalty" : "Add Penalty"}
      subtitle={
        locked
          ? "This entry comes from attendance. Only the payroll cycle can be changed."
          : mode === "edit"
            ? "Update this entry. The penalty amount is recalculated."
            : "Manually add a penalty for an employee."
      }
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={saving ? undefined : onClose}
          primaryLabel={mode === "edit" ? "Save Changes" : "Add"}
          primaryProps={{ onClick: handleSave, loading: saving, disabled: saving || optionsLoading || schedulesLoading }}
        />
      }
    >
      {/* Employees and shifts load first; the form shows once they are in. */}
      {optionsLoading ? <LoaderWithoutHeight info="Loading..." /> : (
      <Mui.Stack spacing={2}>
        {options.errors.length ? <Mui.Alert severity="warning" action={<Mui.Button onClick={() => setRetry((n) => n + 1)}>Retry</Mui.Button>}>{options.errors.join(" ")}</Mui.Alert> : null}
        {scheduleError ? <Mui.Alert severity="warning" action={<Mui.Button onClick={retrySchedules}>Retry</Mui.Button>}>{scheduleError}</Mui.Alert> : null}
        {locked ? (
          <RequestInfoBanner
            title="Penalty details (read only)"
            body="This record is created from attendance. Only the payroll cycle can be changed."
          />
        ) : null}
        <FormSection title="Employee">
          <Mui.Box sx={{
            gridColumn: "1 / -1",
            ...(mode === "edit" ? {
              "& .MuiInputBase-input.Mui-disabled": {
                color: designColors.slate900,
                WebkitTextFillColor: designColors.slate900,
                opacity: 1,
              },
            } : {}),
          }}>
            <Field
              label={required("Employee")}
              helper="Employee the penalty applies to. Search by name or employee code."
              error={visibleError("employee_code")}
            >
              <CustomAutoCompleteWithLogo
                dataList={employeeOptions}
                selectedValue={selectedEmployee}
                updateDataFunction={(event, option) => set("employee_code")(option?.value ?? "")}
                placeHolder="Select employee"
                textBoxError={Boolean(visibleError("employee_code"))}
                disableAction={mode === "edit" || saving || optionsLoading}
                showAvathar={!selectedEmployee}
              />
            </Field>
          </Mui.Box>
        </FormSection>
        {/* Picked employee's card on its own (not inside the white section
            card, which washed out its teal background), same as Team
            Workspace Approvals' Apply panels. */}
        {selectedEmployee ? (
          <SelectedEmployeeHeader
            selectedUser={{
              ...selectedEmployee,
              department_name: selectedEmployee.department_name || initial.department_name,
              designation: selectedEmployee.designation || initial.designation,
            }}
          />
        ) : null}

        <FormSection title="Payroll Details">
          {/* Helpers on paired fields stay one line (≤ ~30 characters): a
              longer one wraps and pushes its input below its neighbour's. */}
          <Field label={required("Payroll Cycle")} helper="Payroll run that deducts this.">
            <SelectField
              value={form.pay_schedule_id}
              onChange={set("pay_schedule_id")}
              options={[{ value: "", label: mode === "edit" ? "Unassigned" : "Automatic" }, ...cycleOptions]}
              disabled={saving || schedulesLoading || !form.employee_code}
              placeholder={schedulesLoading ? "Loading payroll cycles..." : "Select payroll cycle"}
            />
          </Field>
          <Field label="Source" helper="Where this entry came from.">
            <TextField
              value={mode === "edit" ? (locked ? "Attendance" : source || "–") : "Manual"}
              disabled
              onChange={() => {}}
            />
          </Field>
        </FormSection>

        <FormSection title="Penalty Details">
          {/* The date can't be changed once saved (the server refuses it). */}
          <Field label={required("Date")} helper={mode === "edit" ? "Can't be changed once saved." : "Date the penalty applies to."} error={visibleError("date")}>
            <DateField
              value={form.date}
              disabled={locked || saving || mode === "edit"}
              error={visibleError("date")}
              onChange={set("date")}
            />
          </Field>
          <Field label={required("Penalty Type")} helper="Reason for the deduction." error={visibleError("penalty_type")}>
            <SelectField
              value={form.penalty_type}
              disabled={locked || saving}
              onChange={set("penalty_type")}
              error={visibleError("penalty_type")}
              options={toOptions([...new Set([...penaltyTypes, form.penalty_type].filter(Boolean))])}
              placeholder={penaltyTypesLoading ? "Loading penalty types..." : "Select penalty type"}
            />
          </Field>
          <Field label="Shift" helper="Rostered shift (optional)." error={visibleError("shift_name")}>
            <SelectField
              value={form.shift_name}
              disabled={locked || saving}
              onChange={set("shift_name")}
              error={visibleError("shift_name")}
              options={[{ value: "", label: "No shift" }, ...shiftOptions]}
              placeholder="Select shift"
            />
          </Field>
          <Field label={required("Basis")} helper="Per shift, per hour or fixed." error={visibleError("basis")}>
            <SelectField
              value={form.basis}
              disabled={locked || saving}
              onChange={set("basis")}
              error={visibleError("basis")}
              options={toOptions([form.basis].filter((value) => value && !BASIS_OPTIONS.some((option) => option.value === value))).concat(BASIS_OPTIONS)}
              placeholder="Select basis"
            />
          </Field>
          {form.basis === "amount" ? (
            <Field label={required("Amount (₹)")} helper="Fixed amount deducted." error={visibleError("amount")}>
              {numberInput("amount")}
            </Field>
          ) : (
            <>
              <Field
                label={required("Penalty Quantity")}
                helper={form.basis === "hours" ? "Hours deducted (HH:MM)." : "Shifts deducted (0.5 = half)."}
                error={visibleError("quantity")}
              >
                {form.basis === "hours" ? (
                  <TimeField
                    value={form.quantity}
                    disabled={locked || saving}
                    error={visibleError("quantity")}
                    onChange={set("quantity")}
                  />
                ) : numberInput("quantity")}
              </Field>
              <Field label={required("Rate (₹)")} helper="Deducted per shift or hour." error={visibleError("rate")}>
                {numberInput("rate")}
              </Field>
              <Field label={required("Factor")} helper="Multiplier (1 = normal)." error={visibleError("payment_factor")}>
                {numberInput("payment_factor")}
              </Field>
            </>
          )}
          <Field label="Penalty Amount (₹)" helper={form.basis === "amount" ? "Amount deducted." : "Quantity × rate × factor."}>
            <TextField value={formatMoney(totals.payable_amount)} disabled onChange={() => {}} />
          </Field>
        </FormSection>

      </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};

const money = (value) => (value == null || value === "" ? "–" : formatMoney(value));
const basisLabel = (basis) => BASIS_OPTIONS.find((option) => option.value === basis)?.label || basis || "–";

// The View panel's calculation lines, same layout as the Overtime view:
//   Penalty Qty, then Penalty Qty × Factor × Rate = Penalty Amount.
// Hours basis shows the HH:MM as saved plus its decimal hours (00:30 ->
// 0.50 hrs), which is what the formula uses. Amount basis has no quantity or
// rate: just the fixed amount. The amount is the server's, never
// recalculated here.
export const penaltyCalculation = (row) => {
  const hours = row.basis === "hours";
  const qty = quantityValue(row.basis, row.quantity);
  const known = qty != null && Number.isFinite(qty) && String(row.quantity ?? "").trim() !== "";
  const factor = row.payment_factor == null || row.payment_factor === "" ? 1 : Number(row.payment_factor);
  return {
    amountBasis: row.basis === "amount" || (row.rate == null && !known),
    qtyText: !known ? "–" : hours && String(row.quantity).includes(":") ? `${row.quantity} (${formatExactQty(qty)} hrs)` : `${formatExactQty(qty)}${hours ? " hrs" : ""}`,
    formula: `${known ? formatExactQty(qty) : "–"} × ${formatExactQty(factor)} × ${money(row.rate)} = ${money(row.penalty_amount ?? row.payable_amount)}`,
    amount: money(row.penalty_amount ?? row.payable_amount),
  };
};

// showApproval: false when Pay Input Configuration has no approval step
// (no status chip or Approval Status row).
export const PenaltyViewPanel = ({ open, row, onClose, onEdit, onDelete, onStatus, canEdit, showApproval = true, expanded, onToggleExpand, domain, busy }) => {
  const [tab, setTab] = useState("info");
  // Each record opens on its Information tab.
  useEffect(() => setTab("info"), [row?.id]);

  if (!row) return null;
  const { indigo, blue, green, orange, purple, pink } = detailAccentPalette;
  const calc = penaltyCalculation(row);
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title="Penalty"
      subtitle={`${row.penalty_type || "Penalty"} details · ${formatDate(row.date)}`}
      bodySx={panelBodySx}
      headerActions={
        canEdit ? (
          <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
            <span>
              <Mui.IconButton
                size="small"
                aria-label="Edit"
                disabled={busy}
                onClick={() => onEdit(row)}
                sx={{ ...designShell.panelSoftIconButton, color: designColors.tealDark }}
              >
                <EditOutlined fontSize="small" />
              </Mui.IconButton>
            </span>
          </Mui.Tooltip>
        ) : null
      }
      // Delete sits in the footer as a full-width button, like the Cancel
      // action on Team Workspace Approvals' detail panels.
      footer={
        canEdit ? (
          <ViewActionFooter
            approval={showApproval ? { status: row.status, disabled: busy, onApprove: onStatus && (() => onStatus(row, "Approved")), onReject: onStatus && (() => onStatus(row, "Rejected")) } : null}
            recordAction={{ label: "Delete Penalty", onClick: () => onDelete(row), disabled: busy }}
          />
        ) : null
      }
    >
      {/* Employee card, same as Team Workspace Approvals' view panels. */}
      <Mui.Box sx={{ mb: 2 }}>
        <RequestDetailHeader
          userDetails={{
            image_url: row.image_url,
            employee_id: row.employee_code,
            user_name: row.employee_name,
            department_name: row.department_name || row.department,
            designation: row.designation_name || row.designation,
          }}
          status={showApproval ? row.status : undefined}
        />
      </Mui.Box>
      <Mui.Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        variant="fullWidth"
        sx={{
          ...designShell.panelTabs,
          mt: 0,
          mb: 2,
          borderBottom: `1px solid ${designColors.slate200}`,
        }}
      >
        <Mui.Tab label="Information" value="info" />
        <Mui.Tab label="Activity Log" value="audit" />
      </Mui.Tabs>
      {tab === "audit" ? (
        <PayInputActivityLog domain={domain} type="PENALTY" recordId={row.id} refreshKey={row.modifiedat} />
      ) : (
        <Mui.Stack spacing={2}>
          <RequestDetailsSection
            title="PENALTY DETAILS"
            fields={[
              { icon: CalendarMonthOutlined, label: "Date", value: formatDate(row.date), accent: indigo },
              { icon: CategoryOutlined, label: "Penalty Type", value: row.penalty_type || "–", accent: pink },
              { icon: ScheduleOutlined, label: "Shift", value: row.shift_name || "–", accent: blue },
              { icon: BadgeOutlined, label: "Basis", value: basisLabel(row.basis), accent: purple },
            ]}
          />
          <RequestDetailsSection
            title="CALCULATION"
            fields={calc.amountBasis ? [
              { icon: StorageOutlined, label: "Fixed Penalty Amount", value: calc.amount, accent: orange },
            ] : [
              { icon: StorageOutlined, label: "Penalty Qty", value: calc.qtyText, accent: green },
              {
                icon: StorageOutlined,
                label: "Penalty Qty × Factor × Rate = Penalty Amount",
                value: calc.formula,
                accent: orange,
              },
            ]}
          />
          <RequestDetailsSection
            title="PAYROLL"
            fields={[
              ...(row.company_name ? [{ icon: ApartmentOutlined, label: "Company", value: row.company_name, accent: indigo }] : []),
              { icon: EventRepeatOutlined, label: "Payroll Cycle", value: row.payroll_cycle || "–", accent: indigo },
              ...(showApproval ? [{ icon: TaskAltOutlined, label: "Approval Status", value: row.status || "–", accent: blue }] : []),
              { icon: StorageOutlined, label: "Source", value: row.source || "–", accent: purple },
            ]}
          />
        </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
